import type { KyInstance } from 'ky';
import { debounce } from 'lodash';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { FlikHttpAuthOptions } from '../auth';
import { ExponentialBackoffOptions } from '../retries';
import { createKyClient } from './client'; // Renamed from kyClient.ts

interface KyProviderProps {
  uri: () => string;
  auth: FlikHttpAuthOptions;
  backoff?: ExponentialBackoffOptions;
  children: React.ReactNode;
}

const KyContext = createContext<KyInstance | null>(null);

export const KyProvider = ({
  uri,
  auth,
  backoff,
  children,
}: KyProviderProps) => {
  const kyInstance = createKyClient({ uri, auth, backoff });

  return <KyContext.Provider value={kyInstance}>{children}</KyContext.Provider>;
};

export const useKy = (): KyInstance => {
  const context = useContext(KyContext);
  if (!context) {
    throw new Error('useKy must be used within a KyProvider');
  }
  return context;
};

const DEBOUNCE_TIME = 100;

interface BaseRequestOptions {
  path: string;
}

interface GetRequestOptions<TParams> extends BaseRequestOptions {
  method: 'GET';
  parameters?: TParams;
}

interface OtherRequestOptions<TBody, TParams> extends BaseRequestOptions {
  method: 'POST' | 'PUT' | 'DELETE';
  body?: TBody;
  parameters?: TParams;
}

type RequestOptions<TBody, TParams> =
  | GetRequestOptions<TParams>
  | OtherRequestOptions<TBody, TParams>;

const objectToQueryParams = (obj: Record<string, any>): string => {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  return params.toString();
};

// ✅ Debounce function for requests
const createDebouncedSend = () => {
  let pendingRequests: {
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }[] = [];

  const debouncedFunction = debounce(
    async (
      ky: KyInstance,
      requestPath: string,
      method: string,
      options?: {
        parameters?: any;
        body?: any;
        headers?: Record<string, string>;
      },
    ) => {
      try {
        const response = await ky(requestPath, {
          method,
          ...(method !== 'GET' ? { json: options?.body } : {}),
          headers: options?.headers,
        }).json();

        pendingRequests.forEach(({ resolve }) => resolve(response));
      } catch (error) {
        // Ensure the error is properly propagated with response information
        if (error && typeof error === 'object' && 'response' in error) {
          // This is an HTTPError with response information
          pendingRequests.forEach(({ reject }) => reject(error));
        } else {
          // Create a more informative error if the original is malformed
          const enhancedError = new Error(
            error instanceof Error ? error.message : 'Request failed',
          );
          (enhancedError as any).response = { status: 500 };
          pendingRequests.forEach(({ reject }) => reject(enhancedError));
        }
      } finally {
        pendingRequests = []; // Clear pending requests
      }
    },
    DEBOUNCE_TIME,
    { leading: false, trailing: true },
  );

  return (ky: KyInstance, requestPath: string, method: string, options?: any) =>
    new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject });
      debouncedFunction(ky, requestPath, method, options);
    });
};

const debouncedSend = createDebouncedSend();

export const useKyRequest = <TParams, TBody, TResponse>({
  method,
  path,
}: RequestOptions<TBody, TParams>) => {
  const ky = useKy();
  const [data, setData] = useState<TResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const requestPath = path.startsWith('/') ? path.slice(1) : path;

  const send = useCallback(
    async (options?: {
      parameters?: TParams;
      body?: TBody;
      headers?: Record<string, string>;
    }) => {
      setLoading(true);
      setError(null);

      let finalRequestPath = requestPath;

      if (method === 'GET' && options?.parameters) {
        const queryParams = objectToQueryParams(
          options.parameters as Record<string, any>,
        );
        finalRequestPath += queryParams ? `?${queryParams}` : '';
      }

      try {
        const response = await debouncedSend(
          ky,
          finalRequestPath,
          method,
          options,
        );
        setData(response as TResponse);
        return response as TResponse;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [ky, method, requestPath],
  );

  return [send, { data, loading, error }] as const;
};
